import React from 'react'
import {useForm} from '../../common/hooks/useForm'
import {useNotification} from '../../common/notifications/useNotification'
import {resolveCoordinates} from '../../common/utils/resolveCoordinates'
import {AllActivitiesDocument, GQL_AddActivityAreaResponseType, Maybe, useAddActivityAreaMutation} from '../../generated/graphql'
import {Box} from '../../webui/Box'
import {SaveButton} from '../../webui/buttons/SaveButton'
import {MultiSelectProps} from '../../webui/inputs/MultiSelect'
import {TextField, TextFieldProps} from '../../webui/inputs/TextField'
import {LoadingSpinnerContainer} from '../../webui/progress/LoadingSpinnerContainer'
import {Typography} from '../../webui/Typography'
import {AddLocation, LocationForm, LocationTranslationsForm} from '../location/add/AddLocation'
import {buildLocationTranslationsInput} from '../location/add/buildLocationTranslationsInput'
import {NON_CANONICAL_LOCATION_TRANSLATION_LANGUAGES} from '../location/translationLanguages'
import {ActivitiesSelect} from './ActivitiesSelect'
import {AreaInfosForm, EditAreaInfos} from './EditAreaInfos'
import {useActivities} from './gql/useActivities'

interface AddActivityAreaProps {}

interface AddActivityAreaForm {
  type: string
  activities: string[]
}

const initialAddActivityAreaForm: AddActivityAreaForm = {
  type: '',
  activities: [],
}

const initialLocationTranslationsForm: LocationTranslationsForm = NON_CANONICAL_LOCATION_TRANSLATION_LANGUAGES.reduce(
  (acc, lang) => ({...acc, [lang]: {description: ''}}),
  {} as LocationTranslationsForm,
)

const initialLocationForm: LocationForm = {
  name: '',
  description: '',
  lat: '',
  long: '',
  translations: initialLocationTranslationsForm,
}

const initialAreaInfosForm: AreaInfosForm = {
  areaInfos: [],
}

const resolveErrorMessage = (type: Maybe<GQL_AddActivityAreaResponseType> | undefined): string => {
  return 'Could not add activity area'
}

export const AddActivityArea: React.FC<AddActivityAreaProps> = () => {
  const {form, reset, updateField} = useForm<AddActivityAreaForm>(initialAddActivityAreaForm)
  const locationForm = useForm(initialLocationForm)
  const areaInfosForm = useForm(initialAreaInfosForm)

  // TODO: error handling
  const [addActivityArea, {loading}] = useAddActivityAreaMutation()
  const {data: activities, loading: activitiesLoading} = useActivities()

  const {showNotification} = useNotification()

  // TODO: Make select? Autocomplete text input?
  const onTypeChange: TextFieldProps['onChange'] = (value) => {
    updateField('type', value)
  }

  const onActivitiesChange: MultiSelectProps['onChange'] = (value) => {
    updateField('activities', value)
  }
  // TODO: active, verified, areaInfos, type

  const onSave = async () => {
    try {
      const result = await addActivityArea({
        variables: {
          activityAreaInput: {
            activities: form.activities,
            areaInfos: areaInfosForm.form.areaInfos,
            type: form.type,
            location: {
              description: locationForm.form.description,
              name: locationForm.form.name,
              coordinates: resolveCoordinates(locationForm.form.lat, locationForm.form.long),
              translations: buildLocationTranslationsInput(
                locationForm.form.description,
                locationForm.form.translations ?? initialLocationTranslationsForm,
              ),
            },
          },
        },
        refetchQueries: [{query: AllActivitiesDocument}],
      })
      if (result?.data?.addActivityArea?.type === 'Success') {
        reset()
        locationForm.reset()
        showNotification({
          message: 'Activity area added.',
          severity: 'success',
        })
      } else {
        showNotification({
          message: resolveErrorMessage(result?.data?.addActivityArea?.type),
          severity: 'error',
        })
      }
    } catch (e) {
      showNotification({
        message: 'Could not add activity area.',
        severity: 'error',
      })
      console.log('activity area add error', e, '--- e')
    }
  }

  return (
    <Box display={'inline-flex'} flexDirection={'column'}>
      <Typography variant={'h4'}>Add activity area</Typography>
      <Box display={'inline-flex'} flexDirection={'row'}>
        <Box>
          <TextField label={'Type'} onChange={onTypeChange} value={form.type} />
        </Box>
      </Box>
      <Box display={'inline-flex'} flexDirection={'row'}>
        {!activitiesLoading ? <ActivitiesSelect activities={activities} onChange={onActivitiesChange} /> : <LoadingSpinnerContainer />}
      </Box>
      <Box mt={2}>
        <EditAreaInfos form={areaInfosForm.form} updateField={areaInfosForm.updateField} />
      </Box>
      <Box mt={2}>
        <AddLocation form={locationForm.form} updateField={locationForm.updateField} />
      </Box>
      <Box mt={2} display={'flex'} alignSelf={'flex-end'}>
        <SaveButton loading={loading} onClick={onSave} />
      </Box>

      {/* Read-only informational notes box */}
      <Box mt={4} p={2} border="1px solid rgba(0,0,0,0.12)" borderRadius={4} bgcolor="rgba(0,0,0,0.02)" maxHeight={240} overflow="auto">
        <Typography
          variant="body2"
          component="pre"
          sx={{
            whiteSpace: 'pre-wrap',
            fontFamily: 'inherit',
            lineHeight: 1.5,
          }}
        >
          {`
ADDITIONAL INSTRUCTIONS

Adding an activity area still requires some developer assistance for the location
description translations to become visible. After saving via the form above, please
contact the dev team with the description text in each supported language
(en, sv, no, da) — the same texts you entered in the "Description translations"
section.

------------------------------------------------
Instructions for the dev team:

Per-language descriptions are now captured directly on the location (the form's
"Description translations" section writes them to the location's \`translations\` field).
The data is on the row after save. Admin's box page already reads from the row — but
piffl-app and piffl-web do not:

  * piffl-admin reads from the row first (Step 5 of the migration).
  * piffl-app and piffl-web still read \`location.description.<box.name>\` from the bundled
    catalog + db language blob (Steps 3 and 4 not yet shipped).
  * Until those ship (see Steps 3/4 in doc/location-translations-context.md), you still
    need to add \`location.description.<box.name>\` entries to the db language blobs and
    mirror them back to the piffl-app repo's JSON files. The values to enter are the
    exact same ones the non-dev typed into the form above.

Once row-aware reads ship and old app versions decay, this manual blob/catalog step goes
away (Step 8 in the plan).
`}
        </Typography>
      </Box>
    </Box>
  )
}
